import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { StatusChip } from '../../abf/components/StatusChip';
import { Card, INK } from '../../abf/onboarding/review/reviewStyles';
import { type AgreementGroup, type AgreementRow, sourceChip, sourceSettled, sourceSummary } from './agreementReviewModel';

const Group = styled.div`
  border-top: 1px solid ${INK.line};
  &:first-of-type { border-top: 0; }
`;

const GroupHead = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.6rem 0.2rem;
  border: 0;
  background: transparent;
  color: ${INK.body};
  cursor: pointer;
  font: inherit;
  text-align: left;
  &:focus-visible { outline: 2px solid ${INK.accent}; outline-offset: 2px; border-radius: 6px; }
`;

const GroupTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: ${INK.strong};
  font-size: 0.82rem;
  font-weight: 800;
`;

const GroupMeta = styled.span`
  color: ${INK.muted};
  font-size: 0.72rem;
`;

const Rows = styled.div`
  overflow-x: auto;
  padding-bottom: 0.5rem;
`;

const RowButton = styled.button<{ $selected: boolean }>`
  display: grid;
  width: 100%;
  min-width: 30rem;
  grid-template-columns: minmax(8rem, 1fr) minmax(8rem, 1.1fr) minmax(8rem, 1.1fr) 9.5rem;
  & > :last-child { justify-self: end; }
  gap: 0.6rem;
  align-items: center;
  padding: 0.5rem 0.55rem;
  border: 1px solid ${p => p.$selected ? 'rgba(56, 189, 248, 0.5)' : 'transparent'};
  border-radius: 8px;
  background: ${p => p.$selected ? 'rgba(12, 74, 110, 0.2)' : 'transparent'};
  color: ${INK.soft};
  cursor: pointer;
  font: inherit;
  font-size: 0.8rem;
  text-align: left;
  &:hover { background: rgba(30, 41, 59, 0.55); }
  &:focus-visible { outline: 2px solid ${INK.accent}; outline-offset: 1px; }
  .label { color: ${INK.body}; font-weight: 700; }
  .value { color: ${INK.strong}; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  .value small { display: block; color: ${INK.muted}; font-size: 0.72rem; font-weight: 400; margin-top: 2px; }
  .source { color: ${INK.faint}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const NotStated = styled.p`
  margin: 0.2rem 0.55rem 0.4rem;
  color: ${INK.faint};
  font-size: 0.74rem;
  line-height: 1.5;
`;

interface Props {
  groups: AgreementGroup[];
  selectedUid: string | null;
  onSelect: (row: AgreementRow) => void;
  /** Show only the rows whose source still needs a look. */
  onlyUnconfirmed: boolean;
  /** What the document is called: "agreement", "drawdown notice". */
  documentNoun: string;
}

/** Every term of the document, grouped the way it is read, each with its source. */
const AgreementRecordList: React.FC<Props> = ({ groups, selectedUid, onSelect, onlyUnconfirmed, documentNoun }) => {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const list = useRef<HTMLDivElement>(null);
  // A term opened from a link (Ask Axiom, a covenant's clause) can sit far down the list: bring it into view.
  useEffect(() => {
    if (!selectedUid) return;
    const row = Array.from(list.current?.querySelectorAll<HTMLElement>('[data-uid]') ?? []).find(el => el.dataset.uid === selectedUid);
    // Centred: the review's fixed next-step bar covers the bottom edge of the screen.
    row?.scrollIntoView({ block: 'center' });
  }, [selectedUid]);
  const visible = groups
    .map(group => ({
      ...group,
      shown: onlyUnconfirmed ? group.rows.filter(row => !sourceSettled(row.citation)) : group.rows,
    }))
    .filter(group => group.shown.length > 0 || (!onlyUnconfirmed && group.notStated.length > 0));

  if (!visible.length) {
    return <Card><GroupMeta>{onlyUnconfirmed ? 'Every stated value has a confirmed source.' : 'Nothing was extracted from this document.'}</GroupMeta></Card>;
  }

  return (
    <Card ref={list}>
      {visible.map(group => {
        const open = !collapsed[group.name];
        const confirmed = group.rows.filter(row => sourceSettled(row.citation)).length;
        return (
          <Group key={group.name}>
            <GroupHead type="button" aria-expanded={open}
              onClick={() => setCollapsed(current => ({ ...current, [group.name]: open }))}>
              <GroupTitle>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{group.name}</GroupTitle>
              <GroupMeta>
                {group.rows.length} stated
                {group.rows.length > 0 && ` · ${confirmed === group.rows.length ? 'all sources confirmed' : `${confirmed} sources confirmed`}`}
              </GroupMeta>
            </GroupHead>
            {open && (
              <Rows>
                {group.shown.map(row => {
                  const chip = sourceChip(row.citation);
                  return (
                    <RowButton key={row.uid} data-uid={row.uid} type="button" $selected={row.uid === selectedUid}
                      aria-pressed={row.uid === selectedUid} onClick={() => onSelect(row)}>
                      <span className="label">{row.label}</span>
                      <span className="value">
                        {row.value}
                        {row.extractedValue && <small>Corrected from {row.extractedValue}</small>}
                      </span>
                      <span className="source" title={sourceSummary(row.citation)}>{sourceSummary(row.citation)}</span>
                      <StatusChip tone={chip.tone} label={chip.label} />
                    </RowButton>
                  );
                })}
                {!onlyUnconfirmed && group.notStated.length > 0 && (
                  <NotStated>Not stated in the {documentNoun}: {group.notStated.join(', ')}.</NotStated>
                )}
              </Rows>
            )}
          </Group>
        );
      })}
    </Card>
  );
};

export default AgreementRecordList;
